import React, { useState, useEffect, useMemo } from 'react';
import { Meal, MealType } from '../lib/types';
import { formatIsoDate, getDefaultMealType } from '../lib/dateUtils';
import { canonicalizeIngredient, synthesizeDishTitle } from '../lib/ingredientUtils';
import { evaluateFodmapMeal, evaluateFodmapStacking } from '../lib/fodmapEngine';
import { localStore } from '../lib/storage';
import { Sunrise, Sun, Moon, Apple, Coffee, Trash2, ShieldCheck, Check, AlertCircle, AlertTriangle } from 'lucide-react';

interface MealFormProps {
  onSuccess: (meal: Omit<Meal, 'id'>) => void;
  onUpdate?: (meal: Meal) => void;
  onDelete?: (id: string) => void;
  initialMeal?: Meal | null;
  defaultType?: MealType;
  recentMeals?: Meal[];
}

const MEAL_TYPES: { type: MealType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { type: 'Breakfast', label: 'Breakfast', icon: Sunrise },
  { type: 'Lunch', label: 'Lunch', icon: Sun },
  { type: 'Dinner', label: 'Dinner', icon: Moon },
  { type: 'Snack', label: 'Snack', icon: Apple },
  { type: 'Drink', label: 'Drink', icon: Coffee },
];

const COMMON_ANTI_AGGRAVATES = [
  { label: 'Gluten-Free', value: 'Gluten' },
  { label: 'Dairy-Free', value: 'Dairy' },
  { label: 'Allium-Free', value: 'Allium' },
  { label: 'Nut-Free', value: 'Nuts' },
  { label: 'Egg-Free', value: 'Eggs' },
  { label: 'Soy-Free', value: 'Soy' },
];

export const MealForm: React.FC<MealFormProps> = ({
  onSuccess,
  onUpdate,
  onDelete,
  initialMeal,
  defaultType,
  recentMeals,
}) => {
  const [mealType, setMealType] = useState<MealType>(() => initialMeal?.meal_type || defaultType || getDefaultMealType());
  const [description, setDescription] = useState(initialMeal?.description || '');

  const initDate = initialMeal?.timestamp_iso ? new Date(initialMeal.timestamp_iso) : new Date();
  const [entryDate, setEntryDate] = useState(() => formatIsoDate(initDate));
  const [entryTime, setEntryTime] = useState(() => {
    const hh = String(initDate.getHours()).padStart(2, '0');
    const mm = String(initDate.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  const [tags, setTags] = useState<string[]>(() => {
    if (!initialMeal?.ingredients_tags) return [];
    const raw = initialMeal.ingredients_tags.split(',').map((t) => t.trim()).filter(Boolean);
    return Array.from(new Set(raw.map((t) => canonicalizeIngredient(t)).filter(Boolean)));
  });

  const [antiAggravates, setAntiAggravates] = useState<string[]>(() => {
    if (!initialMeal?.anti_aggravates) return [];
    return initialMeal.anti_aggravates
      .split(/[,;]/)
      .map((t) => t.trim())
      .filter(Boolean);
  });

  const [customTagInput, setCustomTagInput] = useState('');
  const [notes, setNotes] = useState(initialMeal?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Compute live FODMAP rating for the current ingredients
  const fodmapResult = useMemo(() => {
    if (!tags || tags.length === 0) return null;
    try {
      return evaluateFodmapMeal(tags, description);
    } catch (err) {
      console.warn('Error evaluating meal FODMAPs:', err);
      return null;
    }
  }, [tags, description]);

  // Compute live 4-6h rolling FODMAP stacking alert
  const stackingAlert = useMemo(() => {
    if (!tags || tags.length === 0) return null;
    try {
      const allRecent = recentMeals && recentMeals.length > 0
        ? recentMeals
        : localStore.getMeals(localStore.getCurrentUserId());
      return evaluateFodmapStacking(tags, allRecent, 5, `${entryDate}T${entryTime}:00`);
    } catch (err) {
      console.warn('Error evaluating FODMAP stacking:', err);
      return null;
    }
  }, [tags, recentMeals, entryDate, entryTime]);

  useEffect(() => {
    if (initialMeal) {
      setMealType(initialMeal.meal_type || getDefaultMealType());
      setDescription(initialMeal.description || '');
      const d = new Date(initialMeal.timestamp_iso);
      if (!isNaN(d.getTime())) {
        setEntryDate(formatIsoDate(d));
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setEntryTime(`${hh}:${mm}`);
      }
      const raw = initialMeal.ingredients_tags
        ? initialMeal.ingredients_tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const canonical = Array.from(new Set(raw.map((t) => canonicalizeIngredient(t)).filter(Boolean)));
      setTags(canonical);

      if (initialMeal.anti_aggravates) {
        setAntiAggravates(
          initialMeal.anti_aggravates
            .split(/[,;]/)
            .map((t) => t.trim())
            .filter(Boolean)
        );
      } else {
        setAntiAggravates([]);
      }

      setNotes(initialMeal.notes || '');
    }
  }, [initialMeal]);

  // Auto-detect anti-aggravates from description
  const handleDescriptionChange = (text: string) => {
    setDescription(text);
    const lower = text.toLowerCase();
    const detected = new Set(antiAggravates);
    if (/gluten[- ]free|\bgf\b|wheat[- ]free/i.test(lower)) detected.add('Gluten');
    if (/dairy[- ]free|\bdf\b|lactose[- ]free/i.test(lower)) detected.add('Dairy');
    if (/allium[- ]free|no garlic|no onion|without garlic|without onion/i.test(lower)) detected.add('Allium');
    if (/nut[- ]free|peanut[- ]free/i.test(lower)) detected.add('Nuts');
    if (/egg[- ]free/i.test(lower)) detected.add('Eggs');
    if (/soy[- ]free|soya[- ]free/i.test(lower)) detected.add('Soy');

    if (detected.size !== antiAggravates.length) {
      setAntiAggravates(Array.from(detected));
    }
  };

  const toggleAntiAggravate = (value: string) => {
    if (antiAggravates.includes(value)) {
      setAntiAggravates(antiAggravates.filter((a) => a !== value));
    } else {
      setAntiAggravates([...antiAggravates, value]);
      // Remove any conflicting positive ingredient tags
      if (value === 'Gluten') {
        setTags(tags.filter((t) => !/^(gluten|wheat|wheat flour)$/i.test(t)));
      } else if (value === 'Dairy') {
        setTags(tags.filter((t) => !/^(dairy|milk|cheese|butter|cream|yogurt|yoghurt|lactose)$/i.test(t)));
      } else if (value === 'Allium') {
        setTags(tags.filter((t) => !/^(garlic|onion|shallot|spring onion)$/i.test(t)));
      }
    }
  };

  const handleAddIngredient = () => {
    const trimmed = customTagInput.trim();
    if (!trimmed) return;
    const canonical = canonicalizeIngredient(trimmed);
    if (canonical && !tags.includes(canonical)) {
      const nextTags = [...tags, canonical];
      setTags(nextTags);
      if (!description.trim()) {
        const auto = synthesizeDishTitle(nextTags);
        if (auto) setDescription(auto);
      }
    }
    setCustomTagInput('');
  };

  const handleRemoveIngredient = (ing: string) => {
    const nextTags = tags.filter((t) => t !== ing);
    setTags(nextTags);
  };

  const handleResetInputs = () => {
    setDescription('');
    setTags([]);
    setAntiAggravates([]);
    setCustomTagInput('');
    setNotes('');
    const now = new Date();
    setEntryDate(formatIsoDate(now));
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setEntryTime(`${hh}:${mm}`);
    setMealType(defaultType || getDefaultMealType());
  };

  const handleSave = () => {
    if (!description.trim()) return;

    setIsSubmitting(true);

    const safeDate = entryDate || formatIsoDate(new Date());
    const safeTime = entryTime || '12:00';
    const d = new Date(`${safeDate}T${safeTime}`);
    const finalIso = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
    const antiStr = antiAggravates.length > 0 ? antiAggravates.join(', ') : undefined;

    if (initialMeal?.id && onUpdate) {
      onUpdate({
        ...initialMeal,
        timestamp_iso: finalIso,
        date: safeDate,
        meal_type: mealType,
        description: description.trim(),
        ingredients_tags: tags.join(', '),
        anti_aggravates: antiStr,
        notes: notes.trim() || undefined,
      });
    } else {
      onSuccess({
        timestamp_iso: finalIso,
        date: safeDate,
        meal_type: mealType,
        description: description.trim(),
        ingredients_tags: tags.join(', '),
        anti_aggravates: antiStr,
        notes: notes.trim() || undefined,
      });
    }
  };

  return (
    <div className="space-y-4 text-left">
      {/* Top Header Actions with Reset Inputs */}
      <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-zinc-800/80">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
          Meal Details
        </span>
        <button
          type="button"
          onClick={handleResetInputs}
          className="text-[11px] font-bold text-slate-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors flex items-center space-x-1"
        >
          <span>Reset Inputs</span>
        </button>
      </div>

      {/* Meal Category (Auto-selected, user can change) */}
      <div className="space-y-1.5">
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
          Meal Category
        </span>
        <div className="grid grid-cols-5 gap-1 p-1 bg-slate-100 dark:bg-zinc-900 rounded-xl border border-slate-200/80 dark:border-zinc-800">
          {MEAL_TYPES.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => setMealType(type)}
              className={`py-1.5 px-1 rounded-lg text-xs font-bold flex flex-col items-center justify-center space-y-0.5 transition-all ${
                mealType === type
                  ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs font-black'
                  : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="text-[9px]">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Dish Name */}
      <div className="space-y-1">
        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
          Dish Name
        </label>
        <input
          type="text"
          required
          placeholder="e.g., Grilled Chicken Salad (Gluten-Free)"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          className="w-full px-3.5 py-2.5 bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
        />
      </div>

      {/* Simple Ingredients Selection */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500">
            Ingredients ({tags.length})
          </label>
          <span className="text-[9px] text-slate-400">Tap to remove</span>
        </div>

        {/* Ingredients Tags */}
        <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800">
          {tags.map((ing) => (
            <button
              key={ing}
              type="button"
              onClick={() => handleRemoveIngredient(ing)}
              className="px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-zinc-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-800 dark:text-zinc-200 hover:text-rose-600 text-xs font-bold flex items-center space-x-1 active:scale-95 transition-all"
            >
              <span>{ing}</span>
              <span className="text-slate-400 hover:text-rose-500">×</span>
            </button>
          ))}
          {tags.length === 0 && (
            <span className="text-xs text-slate-400 dark:text-zinc-500 p-1">No ingredients added yet</span>
          )}
        </div>

        {/* Live FODMAP Safety Badge */}
        {fodmapResult && (
          <div
            className={`p-2.5 rounded-xl border text-xs flex items-center justify-between transition-all ${
              fodmapResult.overallRating === 'low'
                ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200/70 dark:border-emerald-900/50 text-emerald-900 dark:text-emerald-200'
                : fodmapResult.overallRating === 'moderate'
                ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200/70 dark:border-amber-900/50 text-amber-900 dark:text-amber-200'
                : 'bg-rose-50 dark:bg-rose-950/30 border-rose-200/70 dark:border-rose-900/50 text-rose-900 dark:text-rose-200'
            }`}
          >
            <div className="flex items-center space-x-1.5 font-bold">
              {fodmapResult.overallRating === 'low' ? (
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              ) : fodmapResult.overallRating === 'moderate' ? (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              ) : (
                <AlertCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
              )}
              <span className="uppercase text-[10px] tracking-wider">
                {fodmapResult.overallRating} FODMAP
              </span>
              {fodmapResult.flaggedCulprits.length > 0 && (
                <span className="text-[10px] font-medium opacity-80">
                  ({fodmapResult.flaggedCulprits.join(', ')})
                </span>
              )}
            </div>
            {fodmapResult.smartSwaps.length > 0 && (
              <span className="text-[10px] font-semibold opacity-90 truncate max-w-[150px]">
                Tip: Swap {fodmapResult.smartSwaps[0].original}
              </span>
            )}
          </div>
        )}

        {/* Rolling 4-6h Stacking Alert */}
        {stackingAlert?.isStackingWarning && (
          <div className="p-3 rounded-2xl border bg-amber-500/10 dark:bg-amber-950/40 border-amber-500/40 text-amber-900 dark:text-amber-200 text-xs space-y-1.5 animate-fade-in shadow-sm">
            <div className="flex items-center space-x-1.5 font-black text-[11px] text-amber-800 dark:text-amber-300 uppercase tracking-wide">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>Rolling FODMAP Stacking Alert ({stackingAlert.stackedCategories.join(' + ')})</span>
            </div>
            <p className="text-[10px] leading-relaxed text-amber-900/90 dark:text-amber-200/90 font-medium">
              {stackingAlert.warningMessage}
            </p>
          </div>
        )}

        {/* Add ingredient input */}
        <div className="flex items-center space-x-1.5">
          <input
            type="text"
            placeholder="Add ingredient..."
            value={customTagInput}
            onChange={(e) => setCustomTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddIngredient();
              }
            }}
            className="flex-1 px-3 py-2 bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
          />
          <button
            type="button"
            onClick={handleAddIngredient}
            disabled={!customTagInput.trim()}
            className="px-3.5 py-2 rounded-xl bg-slate-950 dark:bg-white text-white dark:text-black font-bold text-xs active:scale-95 disabled:opacity-40 transition-all"
          >
            + Add
          </button>
        </div>
      </div>

      {/* Confirmed Free-From */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 flex items-center space-x-1">
          <ShieldCheck className="w-3 h-3 text-slate-400 dark:text-zinc-500" />
          <span>Confirmed Free-From</span>
        </label>
        <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 dark:bg-zinc-900/60 rounded-2xl border border-slate-200/80 dark:border-zinc-800">
          {COMMON_ANTI_AGGRAVATES.map(({ label, value }) => {
            const isSelected = antiAggravates.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleAntiAggravate(value)}
                className={`px-2.5 py-1 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-all ${
                  isSelected
                    ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs font-black'
                    : 'bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 hover:text-slate-950 dark:hover:text-white border border-slate-200/80 dark:border-zinc-700/80'
                }`}
              >
                <span>{label}</span>
                {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block mb-1">
            Date
          </label>
          <input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block mb-1">
            Time
          </label>
          <input
            type="time"
            value={entryTime}
            onChange={(e) => setEntryTime(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
          />
        </div>
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={handleSave}
        disabled={!description.trim() || isSubmitting}
        className="w-full py-3.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-wider shadow-sm active:scale-[0.98] disabled:opacity-40 transition-all mt-2"
      >
        {isSubmitting ? 'Saving...' : initialMeal?.id ? 'Update Meal' : 'Save Meal'}
      </button>

      {/* Delete Meal Option */}
      {initialMeal?.id && onDelete && !showDeleteConfirm && (
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full py-2.5 text-xs font-bold text-rose-500 hover:text-rose-600 dark:text-rose-400 flex items-center justify-center space-x-1.5 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          <span>Delete Meal Entry</span>
        </button>
      )}

      {/* Delete Confirmation Box */}
      {showDeleteConfirm && initialMeal?.id && onDelete && (
        <div className="p-3 bg-rose-50 dark:bg-rose-950/40 rounded-2xl border border-rose-200 dark:border-rose-800/80 space-y-2 text-center">
          <p className="text-xs text-rose-700 dark:text-rose-300 font-bold">
            Delete this meal entry?
          </p>
          <div className="flex space-x-2 justify-center">
            <button
              type="button"
              onClick={() => onDelete(initialMeal.id)}
              className="px-4 py-1.5 bg-rose-600 text-white rounded-xl text-xs font-bold active:scale-95 transition-all"
            >
              Confirm Delete
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 bg-slate-200 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 rounded-xl text-xs font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
